import { useState } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Hook para manejar eventos del lead
 * @param {Object} detallesLead - Detalles del lead actual
 * @param {Function} setDetallesLead - Función para actualizar detalles del lead
 * @param {Array} contactos - Lista de contactos
 * @param {Function} setContactos - Función para actualizar contactos
 * @param {Function} GuardarComentario - Función para guardar comentarios
 * @param {Function} guardarNotificacion - Función para guardar notificaciones
 * @param {Function} crearObjAction - Función para crear objeto de acción
 * @returns {Object} Objeto con estados y funciones para gestión de eventos
 * @returns {boolean} return.showAgregarEvento - Muestra modal de agregar evento
 * @returns {Function} return.setShowAgregarEvento - Actualiza visibilidad de modal
 * @returns {boolean} return.editarEvento - Indica si se está editando un evento
 * @returns {Function} return.setEditarEvento - Actualiza estado de edición
 * @returns {boolean} return.habilitarBtnEvent - Habilita botón de evento
 * @returns {Function} return.setHabilitarBtnEvent - Actualiza habilitación de botón
 * @returns {boolean} return.showWarningEventoRepetido - Muestra advertencia de evento repetido
 * @returns {Function} return.setShowWarningEventoRepetido - Actualiza advertencia
 * @returns {Array} return.eventosMismaHora - Lista de eventos a la misma hora
 * @returns {Object} return.formDataEvent - Datos del formulario de evento
 * @returns {Function} return.setFormDataEvent - Actualiza datos del formulario
 * @returns {Function} return.handleChange - Maneja cambios en el formulario
 * @returns {Function} return.ValidarEventoRepetido - Valida si hay eventos repetidos
 * @returns {Function} return.RegistrarEvento - Registra o edita un evento
 */
export const useLeadEvents = (detallesLead, setDetallesLead, contactos, setContactos, GuardarComentario, guardarNotificacion, crearObjAction) => {
    const { token, setToken, idUsuario, nombreCompleto } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [showAgregarEvento, setShowAgregarEvento] = useState(false);
    const [editarEvento, setEditarEvento] = useState(false);
    const [habilitarBtnEvent, setHabilitarBtnEvent] = useState(true);
    const [habilitarBtnTerminarEvento, setHabilitarBtnTerminarEvento] = useState(true);
    const [showWarningEventoRepetido, setShowWarningEventoRepetido] = useState(false);
    const [eventosMismaHora, setEventoMismaHora] = useState([]);
    const [formDataEvent, setFormDataEvent] = useState({
        dFechaEvento: new Date(),
        vTituloEvento: '',
        vDescripcionEvento: '',
        idLead: 0,
        idUser: 0,
        idLeadEvento: 0
    });

    const handleChange = (name, value) => {
        setFormDataEvent({
            ...formDataEvent,
            [name]: value
        });
    };

    const ValidarEventoRepetido = async (formData) => {
        if (formData.vTituloEvento == null || formData.vTituloEvento == '') {
            showErrorToast("El titulo es necesario");
            return;
        }
        setHabilitarBtnEvent(false);

        var newDate_ = new Date(formData.dFechaEvento).toLocaleDateString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        });

        newDate_ = newDate_.replace(",", "");
        formData.dFechaEventoReal = newDate_;
        formData.idUsuario = idUsuario;
        formData.idLead = detallesLead.idLead;

        console.log('🔵 [VALIDAR] formData antes de validar:', formData);

        var jsonFormData = JSON.stringify(formData);
        const responseEventoRepetido = await fetch("/api/leads/validarEventoRepetido", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: jsonFormData
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (responseEventoRepetido.ok) {
            const dataEventoRepetido = await responseEventoRepetido.json();

            console.log('🟢 [VALIDAR] Respuesta de validación:', dataEventoRepetido);

            if (dataEventoRepetido.iResponseType == 1) {
                console.log('✅ [VALIDAR] No hay evento repetido, llamando a RegistrarEvento');
                return await RegistrarEvento(formData);
            } else if (dataEventoRepetido.iResponseType == 3) {
                console.log('⚠️ [VALIDAR] Hay eventos a la misma hora');
                var lEventosMismaHora = JSON.parse(dataEventoRepetido.detalles);
                setEventoMismaHora(lEventosMismaHora);
                setShowWarningEventoRepetido(true);
                setShowAgregarEvento(false);
                setHabilitarBtnEvent(true);
            } else {
                showErrorToast(dataEventoRepetido.vMessage);
                setHabilitarBtnEvent(true);
            }
        } else {
            showErrorToast(responseEventoRepetido.status + ' ' + responseEventoRepetido.statusText);
            if (responseEventoRepetido.status == 401) {
                setToken(null);
                location.href = "";
            }
            setHabilitarBtnEvent(true);
        }
    };

    const RegistrarEvento = async (formData) => {
        setHabilitarBtnEvent(false);

        var newDate_ = new Date(formData.dFechaEvento).toLocaleDateString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        });

        newDate_ = newDate_.replace(",", "");
        formData.dFechaEventoReal = newDate_;
        formData.idLead = detallesLead.idLead;
        formData.idUser = idUsuario;
        
        console.log('🔵 [HOOK] Enviando formData al backend:', formData);
        
        var jsonFormData = JSON.stringify(formData);

        const response = await fetch("/api/leads/registrarEvento", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: jsonFormData
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response.ok) {
            const data = await response.json();
            
            console.log('🟢 [HOOK] Respuesta del backend:', data);

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                console.log('🟡 [HOOK] ObjDetalles parseado:', ObjDetalles);
                
                var ObjEventoCreado = ObjDetalles["table2"][0];
                console.log('🟠 [HOOK] ObjEventoCreado (table2[0]):', ObjEventoCreado);

                var date_ = new Date(formData.dFechaEvento).toLocaleDateString('es-mx', {
                    weekday: 'long', year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                });

                var objToActionString = crearObjAction(1, '', 1, detallesLead.idLead);

                if (editarEvento == false) {
                    GuardarComentario("&#x1F7E2;Agregó un evento: <b>" + formData.vTituloEvento + "</b> Descripción: <b>" + formData.vDescripcionEvento + "</b>. Programado para el <b>" + date_ + "</b>", "2", detallesLead.idLead);
                    showSuccessToast("Se ha registrado un evento");

                    if (idUsuario != detallesLead.idUsuario) {
                        guardarNotificacion("Evento con un lead creado",
                            "<b> " + nombreCompleto + "</b> ha creado un evento para el lead <b>#" + detallesLead.idLead + "</b> : <i> '" + formData.vTituloEvento.substring(0, 3) + "...'</i>",
                            detallesLead.idUsuario,
                            objToActionString,
                            2);
                    }
                } else {
                    GuardarComentario("&#x1F7E2;Editó un evento: <b>" + formData.vTituloEvento + "</b> Descripción: <b>" + formData.vDescripcionEvento + "</b>. Programado para el <b>" + date_ + "</b>", "2", detallesLead.idLead);
                    showSuccessToast("Se ha editado un evento");

                    if (idUsuario != detallesLead.idUsuario) {
                        guardarNotificacion("Evento con un lead editado",
                            "<b> " + nombreCompleto + "</b> ha editado un evento con el lead <b>#" + detallesLead.idLead + "</b> : <i> '" + formData.vTituloEvento.substring(0, 3) + "...'</i>",
                            detallesLead.idUsuario,
                            objToActionString,
                            2);
                    }
                }

                if (contactos !== null && typeof contactos !== 'undefined') {
                    var currentLeadIndex = contactos.findIndex((lead) => lead.idLead === detallesLead.idLead);

                    if (currentLeadIndex == -1) {
                        var detalleslead_Copy = detallesLead;
                        setContactos([detalleslead_Copy, ...contactos]);
                    } else {
                        contactos[currentLeadIndex].iDaysDiff = 0;
                    }
                }

                setShowAgregarEvento(false);
                setShowWarningEventoRepetido(false);
                setHabilitarBtnEvent(true);
                setEditarEvento(false);

                console.log('✅ [HOOK] Retornando ObjEventoCreado:', ObjEventoCreado);
                return ObjEventoCreado;
            } else {
                showErrorToast(data.vMessage);
                setHabilitarBtnEvent(true);
                console.error('❌ [HOOK] Error en respuesta:', data.vMessage);
                return null;
            }
        } else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            setHabilitarBtnEvent(true);
            console.error('❌ [HOOK] Error en fetch:', response.status);
            return null;
        }
    };

    /**
     * Termina un evento activo
     * @param {number} idLeadEvento - ID del evento a terminar
     * @param {string} resultadoEvento - Resultado del evento
     * @param {Array} eventosActivos - Lista de eventos activos
     * @param {Function} setEventosActivos - Actualizar eventos activos
     * @param {Object} listasEventos - Objeto con todas las listas de eventos
     */
    const TerminarEvento = async (idLeadEvento, resultadoEvento, eventosActivos, setEventosActivos, listasEventos) => {
        setHabilitarBtnTerminarEvento(false);
        
        if (resultadoEvento == null || resultadoEvento == '') {
            showErrorToast("Es necesario que escriba el resultado del evento");
            setHabilitarBtnTerminarEvento(true); 
            return;
        }

        const response = await fetch("/api/leads/TerminarEvento/?idLeadEvento=" + idLeadEvento + "&vResultado=" + resultadoEvento, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            }
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                showSuccessToast("Se ha marcado el evento como terminado.");
                GuardarComentario("&#x26D4;Terminó el evento con el siguiente resultado: <b>" + resultadoEvento + "</b>", "3", detallesLead.idLead);

                // Remover de eventos activos
                const currentEventIndex = eventosActivos.findIndex((e) => e.idLeadEvento === idLeadEvento);
                if (currentEventIndex >= 0) {
                    const newEventosActivos = [
                        ...eventosActivos.slice(0, currentEventIndex),
                        ...eventosActivos.slice(currentEventIndex + 1)
                    ];
                    setEventosActivos(newEventosActivos);
                }

                // Actualizar otras listas de eventos
                const { 
                    proximoEvento, 
                    setProximoEvento,
                    listaEventos,
                    setListaEventos,
                    listaEventosPasados,
                    setListaEventosPasados,
                    listaEventosHoy,
                    setListaEventosHoy,
                    listaEventosFuturos,
                    setListaEventosFuturos
                } = listasEventos;

                if (proximoEvento !== null && proximoEvento !== undefined) {
                    const currentEventIndexProximoEvento = proximoEvento.findIndex((e) => e.idLeadEvento === idLeadEvento);
                    if (currentEventIndexProximoEvento >= 0) {
                        const newProximoEvento = [
                            ...proximoEvento.slice(0, currentEventIndexProximoEvento),
                            ...proximoEvento.slice(currentEventIndexProximoEvento + 1)
                        ];
                        setProximoEvento(newProximoEvento);
                    }
                }

                if (listaEventos !== null && listaEventos !== undefined) {
                    const currentListEventIndex = listaEventos.findIndex((e) => e.idLeadEvento === idLeadEvento);
                    if (currentListEventIndex >= 0) {
                        const newListaEventosActivos = [
                            ...listaEventos.slice(0, currentListEventIndex),
                            ...listaEventos.slice(currentListEventIndex + 1)
                        ];
                        setListaEventos(newListaEventosActivos);
                    }
                }

                if (listaEventosPasados !== null && listaEventosPasados !== undefined) {
                    const eventoPasadoIndex = listaEventosPasados.findIndex((e) => e.idLeadEvento == idLeadEvento);
                    if (eventoPasadoIndex >= 0) {
                        const newListaEventosPasados = [
                            ...listaEventosPasados.slice(0, eventoPasadoIndex),
                            ...listaEventosPasados.slice(eventoPasadoIndex + 1)
                        ];
                        setListaEventosPasados(newListaEventosPasados);
                    }
                }

                if (listaEventosHoy !== null && listaEventosHoy !== undefined) {
                    const eventoHoyIndex = listaEventosHoy.findIndex((e) => e.idLeadEvento == idLeadEvento);
                    if (eventoHoyIndex >= 0) {
                        const newListaEventosHoy = [
                            ...listaEventosHoy.slice(0, eventoHoyIndex),
                            ...listaEventosHoy.slice(eventoHoyIndex + 1)
                        ];
                        setListaEventosHoy(newListaEventosHoy);
                    }
                }

                if (listaEventosFuturos !== null && listaEventosFuturos !== undefined) {
                    const eventoFuturoIndex = listaEventosFuturos.findIndex((e) => e.idLeadEvento == idLeadEvento);
                    if (eventoFuturoIndex >= 0) {
                        const newListaEventosFuturos = [
                            ...listaEventosFuturos.slice(0, eventoFuturoIndex),
                            ...listaEventosFuturos.slice(eventoFuturoIndex + 1)
                        ];
                        setListaEventosFuturos(newListaEventosFuturos);
                    }
                }

                // Actualizar contactos
                if (contactos !== null && typeof contactos !== 'undefined') {
                    const currentLeadIndex = contactos.findIndex((lead) => lead.idLead === detallesLead.idLead);
                    if (currentLeadIndex == -1) {
                        const detalleslead_Copy = { ...detallesLead, iDaysDiff: 0 };
                        setContactos([detalleslead_Copy, ...contactos]);
                    } else {
                        contactos[currentLeadIndex].iDaysDiff = 0;
                    }
                }

                if (idUsuario != detallesLead.idUsuario) {
                    const objToActionString = crearObjAction(1, '', 1, detallesLead.idLead);
                    guardarNotificacion("Evento de un lead terminado",
                        "<b> " + nombreCompleto + "</b> ha terminado un evento del lead <b>#" + detallesLead.idLead + "</b> : <i> '" + resultadoEvento.substring(0, 5) + "...'</i>",
                        detallesLead.idUsuario,
                        objToActionString,
                        2
                    );
                }

                setDetallesLead({
                    ...detallesLead,
                    iDaysDiff: 0
                });

                setHabilitarBtnTerminarEvento(true);
            } else {
                setHabilitarBtnTerminarEvento(true);
                showErrorToast(data.vMessage);
            }
        } else {
            setHabilitarBtnTerminarEvento(true);
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                window.location.href = "";
            }
        }
    };

    return {
        showAgregarEvento,
        setShowAgregarEvento,
        editarEvento,
        setEditarEvento,
        habilitarBtnEvent,
        setHabilitarBtnEvent,
        habilitarBtnTerminarEvento,
        setHabilitarBtnTerminarEvento,
        showWarningEventoRepetido,
        setShowWarningEventoRepetido,
        eventosMismaHora,
        formDataEvent,
        setFormDataEvent,
        handleChange,
        ValidarEventoRepetido,
        RegistrarEvento,
        TerminarEvento
    };
};
